import { Plus } from "lucide-react";
import type { OrganizationType } from "@bookdental/schemas";
import { resolveDashboardMode } from "@/lib/dashboard-mode";
import type { NavItem } from "../nav-items";

export type ModoDoCelular = "clinica" | "laboratorio" | "hibrida" | "pessoal";

type ChaveDaAba = "inicio" | "menu" | "agenda" | "pacientes" | "pedidos" | "producao" | "equipe" | "organizacoes" | "criar";

export type AbaDoCelular = {
  chave: ChaveDaAba;
  rotulo: string;
  icone: NavItem["icon"];
  /** Ausente só no "+", que abre o painel de criar em vez de navegar. */
  href?: string;
};

/** Uma barra por tipo de organização. Laboratório sem "+": no celular é acompanhamento, não
 *  operação. Pessoal com quatro: a visão que junta as organizações não cria nada por si. */
const ABAS_POR_MODO: Record<ModoDoCelular, ChaveDaAba[]> = {
  clinica: ["inicio", "agenda", "criar", "pacientes", "menu"],
  laboratorio: ["inicio", "menu", "pedidos", "producao", "equipe"],
  hibrida: ["inicio", "agenda", "criar", "pedidos", "menu"],
  pessoal: ["inicio", "organizacoes", "agenda", "menu"],
};

const HREF: Record<Exclude<ChaveDaAba, "criar">, string> = {
  inicio: "/inicio",
  menu: "/menu",
  agenda: "/agenda",
  pacientes: "/pacientes",
  pedidos: "/lab/pedidos",
  producao: "/lab/producao",
  equipe: "/equipe",
  organizacoes: "/organizacoes",
};

export function modoDoCelular(org: { isPersonal: boolean; types: OrganizationType[] } | null | undefined): ModoDoCelular {
  if (!org || org.isPersonal) return "pessoal";
  const modo = resolveDashboardMode(org.types);
  return modo === "lab" ? "laboratorio" : modo === "hybrid" ? "hibrida" : "clinica";
}

/**
 * As abas da barra de baixo. Cada uma sai do item de menu com o mesmo endereço, então herda o
 * filtro de permissão, plano e tipo de organização que o menu já aplica (`useGruposVisiveis`) —
 * aba que o menu esconderia some daqui também, e a barra se redistribui. O "+" só existe se houver
 * ao menos uma ação de criar permitida.
 */
export function abasDoCelular(modo: ModoDoCelular, itensVisiveis: NavItem[], podeCriar: boolean): AbaDoCelular[] {
  const porHref = new Map(itensVisiveis.map((i) => [i.href, i]));
  const abas: AbaDoCelular[] = [];
  for (const chave of ABAS_POR_MODO[modo]) {
    if (chave === "criar") {
      if (podeCriar) abas.push({ chave, rotulo: "Criar", icone: Plus });
      continue;
    }
    const item = porHref.get(HREF[chave]);
    if (item) abas.push({ chave, rotulo: item.label, icone: item.icon, href: item.href });
  }
  return abas;
}

/** Aba da rota atual. Qualquer página fora da barra marca o Menu — sem título no celular, é o que
 *  diz à pessoa que ela está "dentro do Menu". */
export function abaAtiva(pathname: string, abas: AbaDoCelular[]): ChaveDaAba | null {
  const aba = abas.find((a) => a.href && (pathname === a.href || pathname.startsWith(a.href + "/")));
  if (aba) return aba.chave;
  return abas.some((a) => a.chave === "menu") ? "menu" : null;
}
